/**
 * useWritableRegions — the regions the current viewer may create a workshop in.
 * Returned as a `RegionNode[]` TREE so the Region step can render the
 * hierarchical RegionTreeSelect ("Austin" nests under "Texas").
 *
 * The grant that matters is WRITE BREADTH, not a capability: `breadth_write:all`
 * reaches every region, `breadth_write:assigned` reaches the viewer's own
 * regions and everything beneath them. Holding neither means there is nowhere to
 * create, which is returned as an empty list — callers must render that as "no
 * regions", never as "all regions".
 *
 * `loading` and `failed` are reported separately because "none", "not yet" and
 * "we could not find out" are three different answers that all arrive as an
 * empty list. The permissions token, the region tree and the staff profile each
 * settle at their own pace, and any of them can fail outright — a viewer told
 * they may create nowhere because a request errored has been given the wrong
 * reason for the same empty screen.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { RegionNode } from '../../../api/regions';
import { fetchRegions } from '../../../api/regions';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

const STALE_5MIN = 5 * 60_000;

export interface WritableRegions {
  /** Regions the viewer may create in, each carrying its full subtree. */
  regions: RegionNode[];
  /** True until every input the answer depends on has settled. */
  loading: boolean;
  /** A request this answer depends on failed, so the empty list above means
   *  "unknown", not "none". Never true while `loading` is. */
  failed: boolean;
}

/**
 * The assigned regions found anywhere in `tree`, each returned with its subtree
 * intact. A matched node is not descended into: its children are already part
 * of what it grants, and returning them again would list the same region twice.
 */
function assignedSubtrees(tree: RegionNode[], assigned: Set<string>): RegionNode[] {
  const out: RegionNode[] = [];
  const walk = (nodes: RegionNode[]) => {
    for (const node of nodes) {
      if (assigned.has(node.id)) out.push(node);
      else walk(node.children);
    }
  };
  walk(tree);
  return out;
}

interface Inputs {
  permissionsLoading: boolean;
  mayWriteSomewhere: boolean;
  confinedToOwn: boolean;
  treePending: boolean;
  treeMissing: boolean;
  profileLoading: boolean;
  profileMissing: boolean;
}

/**
 * Whether the answer has settled, and whether it settled badly.
 *
 * Split out of the hook so the several ways an empty list can arise are stated
 * in one place rather than as conditions strung through it. The staff profile
 * counts only for a confined viewer — it is where their assigned region ids
 * come from, and a nation-wide viewer never consults it.
 *
 * Either source counts as failed only when it left NOTHING behind. A refetch
 * that fails over a tree already in hand has not made those regions unknown,
 * and reporting it would swap a working picker for an error the viewer cannot
 * act on — react-query keeps the last good data and flags the error alongside
 * it, so the two arrive together.
 */
function settlement(i: Inputs): { loading: boolean; failed: boolean } {
  const profileMatters = i.confinedToOwn;
  const loading =
    i.permissionsLoading ||
    (i.mayWriteSomewhere && i.treePending) ||
    (profileMatters && i.profileLoading);
  const errored = (i.mayWriteSomewhere && i.treeMissing) || (profileMatters && i.profileMissing);
  return { loading, failed: !loading && errored };
}

export function useWritableRegions(): WritableRegions {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { currentUser, loading: profileLoading, error: profileError } = useCurrentUser();

  // Both grants are tested POSITIVELY. `hasPermission` answers false while the
  // token is still decoding, so inferring "assigned" from "not nation-wide"
  // would briefly confine an admin, and inferring "nation-wide" from the
  // absence of both would briefly offer every region to someone entitled to
  // none. Neither grant held is a real answer: nowhere.
  const nationWide = hasPermission(Permission.BREADTH_WRITE_ALL);
  const confinedToOwn = !nationWide && hasPermission(Permission.BREADTH_WRITE_ASSIGNED);
  const mayWriteSomewhere = nationWide || confinedToOwn;

  const regionsQuery = useQuery({
    queryKey: ['regions', 'tree'],
    queryFn: fetchRegions,
    enabled: mayWriteSomewhere,
    staleTime: STALE_5MIN,
  });

  const tree = regionsQuery.data;
  const assignments = currentUser?.regions;

  const regions = useMemo(() => {
    if (!tree) return [];
    if (nationWide) return tree;
    if (!confinedToOwn) return [];
    return assignedSubtrees(tree, new Set((assignments ?? []).map((r) => r.region.id)));
  }, [tree, nationWide, confinedToOwn, assignments]);

  return {
    regions,
    ...settlement({
      permissionsLoading,
      mayWriteSomewhere,
      confinedToOwn,
      treePending: regionsQuery.isPending,
      treeMissing: regionsQuery.isError && tree === undefined,
      profileLoading,
      profileMissing: profileError !== null && currentUser === null,
    }),
  };
}
